import React, { useState, useEffect, useCallback } from 'react';
import { X, Star, Clock, Package, Users, MapPin, ShoppingCart, Loader, Plus, Minus, ChefHat, CircleAlert as AlertCircle, BadgeCheck, Flame, Navigation } from 'lucide-react';
import { GET_FOOD_DETAILS_URL, GET_FOOD_REVIEWS_URL, CART_ADD_URL, CART_VIEW_URL, CART_UPDATE_URL, CART_REMOVE_ITEM_URL } from '../../config/api';
import { authenticatedFetch, FOOD_FALLBACK_IMG, getFirstImageUrl } from '../Utils/apiHelpers';
import { formatFoodCategory } from '../Utils/category';
import { toDate } from '../Utils/time';
import FoodReviewModal from '../FoodReviewModal/FoodReviewModal';
import './FoodDetailModal.css';

interface RatingDist {
  '5_star': number;
  '4_star': number;
  '3_star': number;
  '2_star': number;
  '1_star': number;
}

interface Review {
  review_id?: number;
  user_id?: number;
  user_name?: string;
  user_profile_photo?: string;
  verified_purchase?: boolean;
  rating: number;
  comment?: string;
  created_at?: string;
}

interface FoodDetail {
  food_id: number;
  food_name: string;
  description?: string;
  price: number;
  discount_percentage?: number;
  discounted_price?: number | null;
  category?: string;
  veg_type?: string | null;
  serving_size?: string;
  preparation_time?: string;
  preparation_time_minutes?: number;
  calories?: number | null;
  images?: string[];
  image_urls?: string | string[];
  available_quantity?: number;
  is_available?: boolean;
  stock_status?: string;
  average_rating?: number;
  review_count?: number;
  total_orders?: number;
  rating_distribution?: RatingDist;
  badges?: string[];
  is_featured?: boolean;
  is_bestseller?: boolean;
  is_recommended?: boolean;
}

interface KitchenInfo {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_photo?: string;
  address?: string;
  city?: string;
  state?: string;
  is_online?: boolean;
  is_approved?: boolean;
  distance_km?: number | null;
  estimated_delivery?: string | null;
}

interface ReviewsData {
  food_id: number;
  food_name: string;
  food_image?: string;
  average_rating: number;
  total_reviews: number;
  rating_distribution: RatingDist;
  reviews: Review[];
}

interface FoodDetailModalProps {
  foodId: number;
  onClose: () => void;
  showNotification?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  onCartChange?: () => void;
  canReview?: boolean;
}

const FALLBACK_IMG = FOOD_FALLBACK_IMG;

const StarRow: React.FC<{ rating: number; size?: number }> = ({ rating, size = 14 }) => (
  <div className="fdm-stars-row">
    {[1, 2, 3, 4, 5].map(n => (
      <Star
        key={n}
        size={size}
        className={n <= Math.round(rating) ? 'fdm-star-filled' : 'fdm-star-empty'}
        fill={n <= Math.round(rating) ? 'currentColor' : 'none'}
      />
    ))}
  </div>
);

const RatingBar: React.FC<{ label: string; count: number; total: number }> = ({ label, count, total }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="fdm-bar-row">
      <span className="fdm-bar-label">{label}</span>
      <div className="fdm-bar-track">
        <div className="fdm-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="fdm-bar-count">{count}</span>
    </div>
  );
};

function formatDate(iso?: string): string {
  if (!iso) return '';
  try {
    return toDate(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

const FoodDetailModal: React.FC<FoodDetailModalProps> = ({
  foodId, onClose, showNotification, onCartChange, canReview = false,
}) => {
  const [food, setFood] = useState<FoodDetail | null>(null);
  const [kitchen, setKitchen] = useState<KitchenInfo | null>(null);
  const [reviewsData, setReviewsData] = useState<ReviewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [cartQty, setCartQty] = useState(0);
  const [cartId, setCartId] = useState<number | null>(null);
  const [cartLoading, setCartLoading] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewsKey, setReviewsKey] = useState(0);
  const [latestReviews, setLatestReviews] = useState<Review[]>([]);

  const isLoggedIn = !!localStorage.getItem('authToken');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detailRes, reviewsRes] = await Promise.all([
        authenticatedFetch(GET_FOOD_DETAILS_URL(foodId), { headers: { accept: 'application/json' } }),
        authenticatedFetch(GET_FOOD_REVIEWS_URL(foodId), { headers: { accept: 'application/json' } }),
      ]);

      if (!detailRes.ok) throw new Error(`Could not load food details (${detailRes.status})`);
      const detailData = await detailRes.json();
      if (!detailData.success) throw new Error('Failed to load food details');
      setFood(detailData.food);
      setKitchen(detailData.kitchen ?? null);
      if (detailData.latest_reviews) setLatestReviews(detailData.latest_reviews);

      if (reviewsRes.ok) {
        const revData = await reviewsRes.json();
        if (revData.success) setReviewsData(revData);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load details');
    } finally {
      setLoading(false);
    }
  }, [foodId]);

  const syncCart = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const res = await authenticatedFetch(CART_VIEW_URL);
      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;
      const item = (json.items || []).find((i: any) => i.food_id === foodId);
      if (item) {
        setCartQty(item.quantity);
        setCartId(item.cart_id);
      } else {
        setCartQty(0);
        setCartId(null);
      }
    } catch { /* non-critical */ }
  }, [foodId, isLoggedIn]);

  useEffect(() => {
    fetchData();
    syncCart();
  }, [fetchData, syncCart]);

  useEffect(() => {
    fetchData();
  }, [reviewsKey, fetchData]);

  const handleAdd = async () => {
    if (!isLoggedIn) {
      showNotification?.('info', 'Login Required', 'Please log in to add items to your cart.');
      return;
    }
    setCartLoading(true);
    try {
      const res = await authenticatedFetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ food_id: foodId, quantity: 1, replace_cart: false }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        await syncCart();
        onCartChange?.();
        showNotification?.('success', 'Added', `${food?.food_name} added to cart.`);
      } else if (res.status === 409 || json.message?.toLowerCase().includes('different kitchen')) {
        showNotification?.('warning', 'Different Kitchen', 'Clear your current cart to order from this kitchen.');
      } else {
        showNotification?.('error', 'Failed', json.message || 'Could not add to cart.');
      }
    } catch {
      showNotification?.('error', 'Network Error', 'Please check your connection.');
    } finally {
      setCartLoading(false);
    }
  };

  const handleIncrement = async () => {
    if (!cartId) return;
    setCartLoading(true);
    try {
      const newQty = cartQty + 1;
      const res = await authenticatedFetch(CART_UPDATE_URL(cartId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantity: newQty }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.success) {
        setCartQty(newQty);
        onCartChange?.();
      }
    } catch { /* non-critical */ }
    finally { setCartLoading(false); }
  };

  const handleDecrement = async () => {
    if (!cartId) return;
    setCartLoading(true);
    try {
      if (cartQty <= 1) {
        const res = await authenticatedFetch(CART_REMOVE_ITEM_URL(cartId), { method: 'DELETE' });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success) {
          setCartQty(0);
          setCartId(null);
          onCartChange?.();
        }
      } else {
        const newQty = cartQty - 1;
        const res = await authenticatedFetch(CART_UPDATE_URL(cartId), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantity: newQty }),
        });
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success) {
          setCartQty(newQty);
          onCartChange?.();
        }
      }
    } catch { /* non-critical */ }
    finally { setCartLoading(false); }
  };

  const handleReviewClose = () => {
    setShowReviewModal(false);
    setReviewsKey(k => k + 1);
  };

  const dist = reviewsData?.rating_distribution;
  const totalReviews = reviewsData?.total_reviews ?? 0;
  const avgRating = reviewsData?.average_rating ?? food?.average_rating ?? 0;
  const imageUrl = food?.images?.[0] || getFirstImageUrl(food?.image_urls) || FALLBACK_IMG;

  return (
    <>
      <div className="fdm-overlay" onClick={onClose}>
        <div className="fdm-modal" onClick={e => e.stopPropagation()}>
          <button className="fdm-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>

          {loading && (
            <div className="fdm-loading">
              <Loader size={32} className="fdm-spin" />
              <span>Loading details...</span>
            </div>
          )}

          {error && !loading && (
            <div className="fdm-error">
              <AlertCircle size={24} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && food && (
            <div className="fdm-content">
              {/* Hero image */}
              <div className="fdm-img-wrap">
                <img
                  src={imageUrl}
                  alt={food.food_name}
                  className="fdm-img"
                  onError={e => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                />
                <div className="fdm-img-badges">
                  {food.badges?.map(b => (
                    <span key={b} className="fdm-badge fdm-badge--promo">{b}</span>
                  ))}
                  {food.is_bestseller && <span className="fdm-badge fdm-badge--best">Bestseller</span>}
                  {food.category && (
                    <span className={`fdm-badge fdm-badge--cat fdm-badge--${food.category}`}>
                      {food.category === 'veg' ? '🟢 Vegetarian' : food.category === 'non-veg' ? '🔴 Non-Vegetarian' : formatFoodCategory(food.category)}
                    </span>
                  )}
                  {food.veg_type && food.veg_type !== food.category && (
                    <span className="fdm-badge fdm-badge--veg-type">{food.veg_type}</span>
                  )}
                </div>
                {food.stock_status && (
                  <span className={`fdm-stock-tag fdm-stock-tag--${food.stock_status === 'In Stock' ? 'ok' : 'low'}`}>
                    {food.stock_status}
                  </span>
                )}
              </div>

              <div className="fdm-body">
                {/* Food header */}
                <div className="fdm-food-header">
                  <div className="fdm-food-title-group">
                    <h2 className="fdm-food-name">{food.food_name}</h2>
                    {avgRating > 0 && (
                      <div className="fdm-rating-inline">
                        <StarRow rating={avgRating} size={14} />
                        <span className="fdm-rating-num">{avgRating.toFixed(1)}</span>
                        {totalReviews > 0 && <span className="fdm-rating-total">({totalReviews} reviews)</span>}
                      </div>
                    )}
                    {avgRating === 0 && <span className="fdm-no-rating">No ratings yet</span>}
                  </div>

                  <div className="fdm-price-group">
                    {food.discounted_price ? (
                      <>
                        <span className="fdm-price-discounted">£{food.discounted_price}</span>
                        <span className="fdm-price-original">£{food.price}</span>
                        <span className="fdm-discount-tag">{food.discount_percentage}% off</span>
                      </>
                    ) : (
                      <span className="fdm-price">£{food.price}</span>
                    )}
                  </div>
                </div>

                {/* Description */}
                {food.description && (
                  <p className="fdm-description">{food.description}</p>
                )}

                {/* Meta row */}
                <div className="fdm-meta-grid">
                  {food.preparation_time && (
                    <div className="fdm-meta-item">
                      <Clock size={14} className="fdm-meta-icon" />
                      <span>{food.preparation_time}</span>
                    </div>
                  )}
                  {food.serving_size && (
                    <div className="fdm-meta-item">
                      <Users size={14} className="fdm-meta-icon" />
                      <span>Serves {food.serving_size}</span>
                    </div>
                  )}
                  {food.available_quantity !== undefined && (
                    <div className="fdm-meta-item">
                      <Package size={14} className="fdm-meta-icon" />
                      <span>{food.available_quantity} left</span>
                    </div>
                  )}
                  {food.calories !== undefined && food.calories !== null && (
                    <div className="fdm-meta-item">
                      <Flame size={14} className="fdm-meta-icon" />
                      <span>{food.calories} cal</span>
                    </div>
                  )}
                  {food.total_orders !== undefined && food.total_orders > 0 && (
                    <div className="fdm-meta-item">
                      <ShoppingCart size={14} className="fdm-meta-icon" />
                      <span>{food.total_orders} ordered</span>
                    </div>
                  )}
                </div>

                {/* Kitchen info */}
                {kitchen && (
                  <div className="fdm-kitchen-card">
                    {kitchen.kitchen_photo ? (
                      <img
                        src={kitchen.kitchen_photo}
                        alt={kitchen.kitchen_name}
                        className="fdm-kitchen-photo"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <div className="fdm-kitchen-photo-fallback">
                        <ChefHat size={20} />
                      </div>
                    )}
                    <div className="fdm-kitchen-info">
                      <span className="fdm-kitchen-name">{kitchen.kitchen_name}</span>
                      {(kitchen.address || kitchen.city) && (
                        <span className="fdm-kitchen-addr">
                          <MapPin size={11} />
                          {[kitchen.address, kitchen.city, kitchen.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                      {(kitchen.distance_km !== undefined && kitchen.distance_km !== null || kitchen.estimated_delivery) && (
                        <span className="fdm-kitchen-extra">
                          {kitchen.distance_km !== undefined && kitchen.distance_km !== null && (
                            <><Navigation size={10} /> {kitchen.distance_km.toFixed(1)} km</>
                          )}
                          {kitchen.estimated_delivery && (
                            <><Clock size={10} style={{ marginLeft: '0.5rem' }} /> {kitchen.estimated_delivery}</>
                          )}
                        </span>
                      )}
                    </div>
                    <span className={`fdm-kitchen-status ${kitchen.is_online ? 'fdm-kitchen-status--online' : 'fdm-kitchen-status--offline'}`}>
                      {kitchen.is_online ? 'Online' : 'Offline'}
                    </span>
                  </div>
                )}

                {/* Cart controls */}
                {isLoggedIn && (
                  <div className="fdm-cart-section">
                    {cartQty > 0 ? (
                      <div className="fdm-qty-controls">
                        <button className="fdm-qty-btn" onClick={handleDecrement} disabled={cartLoading}>
                          <Minus size={16} />
                        </button>
                        <span className="fdm-qty-val">
                          {cartLoading ? <Loader size={14} className="fdm-spin" /> : cartQty}
                        </span>
                        <button className="fdm-qty-btn" onClick={handleIncrement} disabled={cartLoading}>
                          <Plus size={16} />
                        </button>
                        <span className="fdm-qty-label">in cart</span>
                      </div>
                    ) : (
                      <button className="fdm-add-btn" onClick={handleAdd} disabled={cartLoading || !food.is_available}>
                        {cartLoading ? <Loader size={16} className="fdm-spin" /> : <ShoppingCart size={16} />}
                        {cartLoading ? 'Adding...' : food.is_available ? 'Add to Cart' : 'Unavailable'}
                      </button>
                    )}
                    {canReview && (
                      <button className="fdm-review-btn" onClick={() => setShowReviewModal(true)}>
                        <Star size={14} />
                        Write a Review
                      </button>
                    )}
                  </div>
                )}

                {/* Ratings section */}
                {reviewsData && (
                  <div className="fdm-reviews-section">
                    <h3 className="fdm-section-title">Ratings &amp; Reviews</h3>
                    {totalReviews > 0 ? (
                      <>
                        <div className="fdm-rating-summary">
                          <div className="fdm-rating-big">
                            <span className="fdm-avg-num">{avgRating.toFixed(1)}</span>
                            <StarRow rating={avgRating} size={18} />
                            <span className="fdm-total-label">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</span>
                          </div>
                          {dist && (
                            <div className="fdm-dist-bars">
                              {([5, 4, 3, 2, 1] as const).map(n => (
                                <RatingBar
                                  key={n}
                                  label={`${n}★`}
                                  count={dist[`${n}_star` as keyof RatingDist]}
                                  total={totalReviews}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="fdm-review-list">
                          {reviewsData.reviews.map((rev, i) => (
                            <div key={rev.review_id ?? i} className="fdm-review-item">
                              <div className="fdm-review-top">
                                <div className="fdm-reviewer-avatar">
                                  {rev.user_profile_photo && rev.user_profile_photo !== 'string' ? (
                                    <img src={rev.user_profile_photo} alt={rev.user_name || 'User'} className="fdm-reviewer-avatar-img" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                  ) : (
                                    (rev.user_name || 'U').charAt(0).toUpperCase()
                                  )}
                                </div>
                                <div className="fdm-reviewer-meta">
                                  <span className="fdm-reviewer-name">
                                    {rev.user_name || 'Anonymous'}
                                    {rev.verified_purchase && (
                                      <span className="fdm-verified-badge" title="Verified Purchase">
                                        <BadgeCheck size={12} /> Verified
                                      </span>
                                    )}
                                  </span>
                                  <div className="fdm-review-rating-date">
                                    <StarRow rating={rev.rating} size={12} />
                                    {rev.created_at && (
                                      <span className="fdm-review-date">{formatDate(rev.created_at)}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              {rev.comment && (
                                <p className="fdm-review-comment">{rev.comment}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="fdm-no-reviews">
                        <Star size={32} className="fdm-no-reviews-icon" />
                        <p>No reviews yet. Be the first to share your experience!</p>
                        {canReview && isLoggedIn && (
                          <button className="fdm-review-btn fdm-review-btn--center" onClick={() => setShowReviewModal(true)}>
                            Write a Review
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showReviewModal && food && (
        <FoodReviewModal
          foodId={foodId}
          foodName={food.food_name}
          onClose={handleReviewClose}
          showNotification={showNotification ?? (() => {})}
        />
      )}
    </>
  );
};

export default FoodDetailModal;
